import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { FormControlChildAbstract } from '~/component/form/abstracts/control-child.abstract';
import { isChildOf } from '~/component/helpers/html.helper';
import { LinkedList } from '~/core/classes/linked-list';
import { generateUid } from '~/core/helpers/uid/uid.helper';

import { DirectionEnum } from './enums/direction.enum';
import { DropdownOption } from './interfaces/option.interface';

/**
 * @see https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html
 */

@Component({
  selector: 'app-form-dropdown',
  templateUrl: './dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FormDropdownComponent,
      multi: true,
    },
  ],
})
export class FormDropdownComponent<T>
  extends FormControlChildAbstract<T>
  implements OnInit, OnDestroy, OnChanges
{
  @Input() public options: DropdownOption[];

  public id = generateUid();
  public isOpen = false;
  public linkedListOption: LinkedList<DropdownOption>;
  public selectedOption: DropdownOption;

  constructor(
    private readonly dropdownComponent: ElementRef,
    @Inject(DOCUMENT) protected readonly document: Document
  ) {
    super();
  }

  /**
   * KEEP IT AS AN ATTRIBUTE
   * Otherwise, we can't access `this` through the listener
   */
  protected readonly clickOutListener = ({ target }): void => {
    const dropdownElement: HTMLElement = this.dropdownComponent.nativeElement;
    if (target !== dropdownElement && !isChildOf(target, dropdownElement)) {
      this.isOpen = false;
    }
  };

  public ngOnChanges(): void {
    this.linkedListOption = new LinkedList<DropdownOption>(this.options || []);

    if (this.selectedOption) {
      this.updateSelected(
        this.options.find(
          (option) => option.value === this.selectedOption.value
        )
      );
    }
  }

  public ngOnDestroy(): void {
    this.document.body.removeEventListener(
      'click',
      this.clickOutListener,
      false
    );
  }

  public ngOnInit(): void {
    this.linkedListOption = new LinkedList<DropdownOption>(this.options);

    this.document.body.addEventListener('click', this.clickOutListener, false);

    let selected = this.options.find((option) => option.value === this.value);

    if (!selected) {
      // If value does not exist in option, select the first one
      selected = this.options[0];
    }

    this.updateSelected(selected);
  }

  public get directionEnum(): typeof DirectionEnum {
    return DirectionEnum;
  }

  public get triggerLabel(): string {
    return this.selectedOption?.text || '';
  }

  protected updateSelected(option: DropdownOption): void {
    // clean all options
    this.linkedListOption.forEach((_option) => (_option.selected = false));

    if (!option) {
      return;
    }

    const selectedOptionIndex = this.options.findIndex(
      (opt) => opt.value === option.value
    );

    if (selectedOptionIndex) {
      this.linkedListOption.setIndex(selectedOptionIndex);
    }

    this.selectedOption = option;
    this.selectedOption.selected = true;

    this.value = option.value;
  }

  public hideMenu(): void {
    this.isOpen = false;
  }

  public navigate($event: Event, direction: DirectionEnum): void {
    $event.preventDefault(); // Prevent scroll

    if (!this.isOpen) {
      this.showMenu();
    }

    switch (direction) {
      case DirectionEnum.Up:
        this.linkedListOption.prevFilter('disabled', false);
        break;
      case DirectionEnum.First:
        this.linkedListOption.firstFilter('disabled', false);
        break;
      case DirectionEnum.Last:
        this.linkedListOption.lastFilter('disabled', false);
        break;
      default:
        this.linkedListOption.nextFilter('disabled', false);
    }

    this.setOption(this.linkedListOption.currentElement, false);
  }

  public setOption(option: DropdownOption, close = true): void {
    if (option.disabled) {
      return;
    }

    this.updateSelected(option);

    if (close) {
      this.hideMenu();
    }
  }

  public showMenu(): void {
    this.isOpen = true;
  }

  public toggleMenu(): void {
    this.isOpen = !this.isOpen;
  }

  public writeValue(value: T): void {
    this.innerValue = value;

    if (value) {
      this.updateSelected(
        this.options.find((option) => option.value === value)
      );
    }
  }
}
